import axios, { AxiosError } from 'axios';
import { loadingService } from './LoadingService';
import { toastService } from './ToastService';

/** Backend-origin (zonder /api). Leeg = zelfde origin (productie achter nginx). */
export const backendBaseUrl: string = (import.meta.env.VITE_BACKEND_URL || '').replace(/\/$/, '');

export const api = axios.create({
  baseURL: `${backendBaseUrl}/api`,
  timeout: 185_000, // AI-/ai-parser-calls duren lang
  headers: { 'Content-Type': 'application/json' },
});

let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: () => void) {
  onUnauthorized = fn;
}

export function setAuthToken(token: string | null) {
  if (token) api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  else delete api.defaults.headers.common['Authorization'];
}

api.interceptors.request.use((config) => {
  if (!(config as any).silent) loadingService.start();
  return config;
});

api.interceptors.response.use(
  (response) => {
    if (!(response.config as any).silent) loadingService.stop();
    return response;
  },
  (error: AxiosError<any>) => {
    if (!(error.config as any)?.silent) loadingService.stop();
    if (error.response?.status === 401) {
      onUnauthorized?.();
    } else if (!(error.config as any)?.noToast) {
      toastService.error(extractErrorMessage(error));
    }
    return Promise.reject(error);
  },
);

/** Haalt de Nederlandse foutmelding uit een API-fout. */
export function extractErrorMessage(err: unknown, fallback = 'Er is een fout opgetreden'): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as any;
    if (data?.error?.message) {
      const details = data.error.details;
      if (Array.isArray(details) && details.length && details[0]?.message) {
        return `${data.error.message}: ${details.map((d: any) => (d.path ? `${d.path}: ` : '') + d.message).join('; ')}`;
      }
      return data.error.message;
    }
    if (err.code === 'ECONNABORTED') return 'De aanvraag duurde te lang (timeout)';
    if (!err.response) return 'Geen verbinding met de server';
    return `${fallback} (${err.response.status})`;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

/** Axios-config-uitbreiding: `silent` = geen loading-balk, `noToast` = geen automatische fout-toast. */
declare module 'axios' {
  export interface AxiosRequestConfig {
    silent?: boolean;
    noToast?: boolean;
  }
}
